import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { RequestsResponse, RequestSummary, RequestOrderRef } from '@shared/api-responses';
import { PROGRAM_LABELS, REQUEST_STATUS_LABELS, type OrderStatus } from '@shared/types';
import { apiFetch } from '../hooks/useApi';
import ProgramBadge from '../components/ProgramBadge';

/**
 * Order progress in the submitter's terms. "Draft" means nothing to crew -- to
 * them the order has simply arrived with logistics.
 */
function progressLabel(o: RequestOrderRef): string {
  const labels: Record<OrderStatus, string> = {
    draft: 'Received',
    submitted: 'In progress',
    approved: 'In progress',
    placed: o.channel === 'vendor' ? 'Sent to vendor' : 'Shopped',
    received: 'Delivered',
    cancelled: 'Cancelled',
  };
  return labels[o.status] ?? o.status;
}

/** Which list an order is: the kitchen shopping list, or a vendor's order. */
function orderName(o: RequestOrderRef): string {
  return o.channel === 'vendor'
    ? `${o.vendor_name ?? 'Vendor'} order`
    : `${PROGRAM_LABELS[o.program] ?? o.program} shopping list`;
}

const PROGRESS_COLOR: Record<string, string> = {
  Waiting: 'text-ems-amber',
  Delivered: 'text-ems-green',
  Cancelled: 'text-zinc-500',
};

/** One word for the whole request: the furthest-behind of its live orders. */
function overall(r: RequestSummary): string {
  const orders = r.orders ?? [];
  if (orders.length === 0) return REQUEST_STATUS_LABELS[r.status] ?? r.status;
  const live = orders.filter((o) => o.status !== 'cancelled');
  if (live.length === 0) return 'Cancelled';
  const order: OrderStatus[] = ['draft', 'submitted', 'approved', 'placed', 'received'];
  const behind = live.reduce((a, b) => (order.indexOf(a.status) <= order.indexOf(b.status) ? a : b));
  return progressLabel(behind);
}

/**
 * The caller's own submissions, each with the order(s) it became and how far
 * along they are. Reads `GET /api/requests?mine=1`, which scopes to
 * the session rather than a query param, so this is safe for any role — crew
 * can't reach the all-stations list.
 */
export default function MyRequests() {
  const [requests, setRequests] = useState<RequestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch<RequestsResponse>('/requests?mine=1')
      .then((r) => setRequests(r.requests))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-white">My requests</h1>
        <Link to="/request" className="rounded-lg bg-dcvfd-accent px-4 py-2 text-sm font-semibold text-white shrink-0">
          New request
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-ems-red/15 border border-ems-red/40 px-3 py-2 text-sm text-ems-red">
          Couldn’t load your requests. Try again.
        </div>
      )}

      {loading ? (
        <p className="text-zinc-400">Loading…</p>
      ) : requests.length === 0 && !error ? (
        <p className="text-zinc-500">
          You haven’t submitted any requests yet. Tap <span className="text-zinc-300">New request</span> to start one.
        </p>
      ) : (
        <div className="space-y-2">
          {requests.map((r) => (
            <div key={r.id} className="glass rounded-lg px-4 py-3 flex items-start justify-between gap-3 text-white">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{r.station_name}</span>
                  {/* Null for anything submitted before the program picker existed. */}
                  {r.program && <ProgramBadge program={r.program} />}
                </div>
                <div className="text-xs text-zinc-400">
                  {new Date(r.submitted_at).toLocaleString()} · {r.item_count} items
                </div>
                {r.note && <div className="mt-1 text-xs text-zinc-500 truncate">{r.note}</div>}
                {/* A request with kitchen and coffee items becomes two orders. */}
                {(r.orders ?? []).map((o) => (
                  <div key={o.id} className="mt-1 text-xs text-zinc-300">
                    {orderName(o)} #{o.id} · <span className="text-zinc-400">{progressLabel(o)}</span>
                  </div>
                ))}
              </div>
              <span className={`text-sm font-semibold shrink-0 ${PROGRESS_COLOR[overall(r)] ?? 'text-white'}`}>
                {overall(r)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
